import React, { useCallback, useEffect, useState, useMemo } from 'react';

// Components
import Bar from './components/Bar';
import List from './components/List';
import Filter from './components/Filter';
import Gallery from './components/Gallery';
import ButtonList from 'components/ButtonList';
import Accordion from 'components/UiKit/Accordion';
import PricesOnAsking from 'components/PricesOnAsking';
import PaginationView from 'components/PaginationView';
import CustomMessage from 'components/Messages/CustomMessage';

// Classname
import clsx from 'clsx';

// Hooks
import { useStructureHandler } from 'pages/Search/useHandlers/useStructureHandler';

// Constants
import { sizeOptions20 } from 'constans/options';
import { arrayFilterNumber } from 'constans/search';

// Lib
import * as yup from 'yup';
import { useForm } from 'react-hook-form';
import { getValueSize } from 'lib/helpers';
import { shallowEqual } from 'react-redux';
import { getDriverInstance } from 'lib/driver';
import { useNavigate } from 'react-router-dom';
import { yupResolver } from '@hookform/resolvers/yup';
import { useAppSelector } from 'lib/hooks/useDispatch';
import { useRegisterTutorialKey } from 'lib/hooks/useTutorial';

// Type
import {
	createTabsConfig,
	typeStructureSearch
} from 'pages/Search/utils';

const numberRange = (minKey: string, maxKey: string, label: string) => ({
	[`${minKey}`]: yup.string().matches(/^[\d|.]*$/gi, `${label} min must be a Number`).test({
		name: 'max',
		exclusive: true,
		params: { max: yup.ref(maxKey) },
		message: 'Min must be less than the max ${max}',
		test: (value = '', ctx) => {
			const max = ctx.parent[maxKey] ? +ctx.parent[maxKey] : false;
			return !max || +value <= max;
		}
	}),
	[`${maxKey}`]: yup.string().matches(/^[\d|.]*$/gi, `${label} max must be a Number`).test({
		name: 'min',
		exclusive: true,
		params: { min: yup.ref(minKey) },
		message: 'Max must be greater than min ${min}',
		test: (value, ctx) => {
			const min = ctx.parent[minKey] ? +ctx.parent[minKey] : false;
			return !min || !value || +value >= min;
		}
	})
});

// Schema
const schema = yup.object({
	linkers: yup.boolean().nullable(),
	compounds: yup.boolean().nullable(),
	searchSimilarity: yup.number().nullable(),
	typeStructure: yup.string().required('Please select the type of search'),

	...numberRange('hbaGte', 'hbaLte', 'HBA'),
	...numberRange('hbdGte', 'hbdLte', 'HBD'),
	...numberRange('tpsaGte', 'tpsaLte', 'TPSA'),
	...numberRange('fsp3Gte', 'fsp3Lte', 'Fsp3'),
	...numberRange('logPGte', 'logPLte', 'cLogP'),
	...numberRange('heavyAtomCountGte', 'heavyAtomCountLte', 'HAC'),
	...numberRange('molecularWeightGte', 'molecularWeightLte', 'MW'),
	...numberRange('rotatableBondCountGte', 'rotatableBondCountLte', 'RotB')
});
type FormData = yup.InferType<typeof schema>;

const Structure: React.FC = () => {
	const navigate = useNavigate();
	const registerTutorialKey = useRegisterTutorialKey();

	const drv = getDriverInstance();

	const {
		data,
		typeData,
		structureGoTo,
		loadingLinkers,
		loadingCompounds,
		marvinStructure,
	} = useAppSelector(
		(store) => {
			return {
				data: store.search.structureSearch.structureData,
				typeData: store.search.structureSearch.structureType,
				structureGoTo: store.search.structureSearch.structureGoTo,

				marvinStructure: store.search.marvinStructure,

				loadingLinkers: store.search.structureSearch.structureLoading.loadingLinkers,
				loadingCompounds: store.search.structureSearch.structureLoading.loadingCompounds,
			};
		},
		shallowEqual
	);

	const size = data && typeData && (data as Record<any, any>)[typeData]?.size;
	const number = data && typeData && (data as Record<string, any>)[typeData]?.number;
	const content = data && typeData ? (data as Record<string, any>)[typeData]?.content : [];
	const totalPages = data && typeData && (data as Record<string, any>)[typeData]?.totalPages;
	const goTo = structureGoTo && typeData && (structureGoTo as Record<string, any>)[typeData].goTo;

	const configMarvin = localStorage.getItem('configMarvin') || '';
	const convertConfigMarvin = !!configMarvin?.length ? JSON.parse(configMarvin || '') : '';

	for (const key in convertConfigMarvin) {
		if (arrayFilterNumber.includes(key) && convertConfigMarvin[key]) {
			convertConfigMarvin[key] = +convertConfigMarvin[key];
		}
	}

	const defaultValues = {
		linkers: false,
		compounds: false,
		searchSimilarity: '0.8',
		typeStructure: 'similarity',
		...convertConfigMarvin
	};

	const [sketcher, setSketcher] = useState<any>(null);
	const [isOpen, setIsOpen] = useState<boolean>(false);
	const [loading, setLoading] = useState<boolean>(true);
	const [isShowTabs, setIsShowTabs] = useState<boolean>(false);
	const [marvinInstance, setMarvinInstance] = useState<any>(null);

	const {
		watch,
		reset,
		control,
		register,
		getValues,
		handleSubmit,
		formState: { errors }
	} = useForm<FormData>({
		resolver: yupResolver(schema),
		defaultValues
	});

	const linkers = watch('linkers') || false;
	const compounds = watch('compounds') || false;

	useEffect(() => {
		registerTutorialKey('structure-search', () => !loading, 'normal', 'general');
		registerTutorialKey('structure-search-list', () => !!content?.length, 'normal', 'general');
	}, [loading, registerTutorialKey]);

	useEffect(() => {
		const molecule = localStorage.getItem('currentValueMarvin');
		if (molecule) {
			sketcher && sketcher.importStructure('smile', marvinStructure ? marvinStructure : molecule || '');
		}
	}, [sketcher, marvinStructure]);

	useEffect(() => {
		window.onpopstate = (e) => {
			setIsShowTabs(true);
		};
	}, []);

	useEffect(() => {
		setIsShowTabs(true);
	}, [navigate]);

	const onLoad = useCallback((e: any) => {
		const marvinWindow = e?.target?.contentWindow?.marvin;

		marvinWindow.onReady(() => {
			setLoading(!loading);
			setMarvinInstance(marvinWindow);
			setSketcher(marvinWindow.sketcherInstance);
		});
	}, []);

	const {
		onSubmit,
		onPagePress,
		handleClickType,
		onNextPressLast,
		onPrevPressLast,
		onKeyPress,
		handlerSubmitGoTo,
		handlerChangeGoTo,
		handlerChangeSize
	} = useStructureHandler({
		sketcher,
		typeData,
		totalPages,
		goTo,
		errors,
		getValues,
		drv
	});

	const handlerToggle = () => {
		setIsOpen(!isOpen);
	};

	const header = <Bar
		errors={errors}
		control={control}
		register={register}
	/>;
	const headerEnd = <>Latest search</>;

	const cnAccordion = clsx(
		'search-screen__structure-bg'
		// isOpen ? 'search-screen__structure-bg--active' : ''
	);

	const configTabs = useMemo(
		() => createTabsConfig({
			data,
			isShow: true,
			types: typeStructureSearch,
			loadingByType: {
				linkers: loadingLinkers,
				compounds: loadingCompounds
			}
		}),
		[data]
	);

	const valueSize = useMemo(
		() => getValueSize({ opt: size, options: sizeOptions20 }),
		[size]
	);

	return (
		<div className="search-screen__structure">
			<form
				className="flex flex-ai-start"
				onSubmit={handleSubmit(onSubmit)}
			>
				<div className={cnAccordion}>
					<Accordion
						isOpen
						header={header}
						headerEnd={headerEnd}
						callBack={handlerToggle}
					>
						<Gallery sketcher={sketcher} />
					</Accordion>

					<div className="search-screen__structure-wrapper" id="structure-search-marvin-js">
						<iframe
							title="Marvin"
							onLoad={onLoad}
							src="/sketch.html"
							className="search-screen__structure-marvin"
						/>
					</div>

					<Filter
						reset={reset}
						errors={errors}
						control={control}
						linkers={linkers}
						typeTab={typeData}
						register={register}
						compounds={compounds}
					/>
				</div>
			</form>

			<ButtonList
				type={typeData}
				config={configTabs}
				onClickType={handleClickType}
				uniqId="structure-search-tabs"
				className="search-screen__btn-tab flex flex-ai-center mt10 flex-direction-column-mobile"
			/>

			{isShowTabs && <div className="search-screen__tabs">
				{((totalPages || 0) > 1) && content?.length && <PaginationView
					isWhite
					isShowSelect

					goTo={goTo}
					onKeyPress={onKeyPress}
					handlerSubmitGoTo={handlerSubmitGoTo}
					handlerChangeGoTo={handlerChangeGoTo}

					number={number}
					totalPages={totalPages}
					onPagePress={onPagePress}
					onNextPressLast={onNextPressLast}
					onPrevPressLast={onPrevPressLast}

					valueSize={valueSize}
					optionsSize={sizeOptions20}
					handlerChangeSize={handlerChangeSize}
				/>}
			</div>}

			{isShowTabs && <List
				data={content}
				type={typeData}
			/>}

			{((totalPages || 0) > 1) && content?.length && <div className="d-flex flex-ai-center flex-jc-between mt10">
				<PricesOnAsking />
				<PaginationView
					isWhite

					goTo={goTo}
					onKeyPress={onKeyPress}
					handlerSubmitGoTo={handlerSubmitGoTo}
					handlerChangeGoTo={handlerChangeGoTo}

					number={number}
					totalPages={totalPages}
					onPagePress={onPagePress}
					onNextPressLast={onNextPressLast}
					onPrevPressLast={onPrevPressLast}

					valueSize={valueSize}
					optionsSize={sizeOptions20}
					handlerChangeSize={handlerChangeSize}
				/>
			</div>}

			{content?.length
				? <CustomMessage
					title="Notice"
					className="mt30"
					text="Due to the dynamic change of the Restricted Product list, if the above material is included in the Restricted Product list or regulated as controlled material by the government, we will not sell it."
				/>
				: null}
		</div>
	);
};
export default Structure;